import { Box, TextField } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, {
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import Tooltip from '../Tooltip';
import useKeyboardFocus from '../../lib/useKeyboardFocus';
import { getCanvasFont, getWidth } from './utils/elementText';

interface EditableTextLabelProps {
  id: string;
  ariaLabel: string;
  value: string;
  disabled?: boolean;
  tooltipTitle?: string;
  onChange: (value: string) => void;
  size?: 'sm' | 'md';
  maxLength?: number;
}

const useStyles = makeStyles<{ inputWidth?: number; size?: string }>()(
  (theme, { inputWidth, size }) => ({
    root: {
      fontSize: size === 'sm' ? theme.spacing(1.5) : theme.spacing(1.75),
      borderRadius: theme.spacing(0.5),
      '&:not(&.Mui-disabled)': {
        '&:hover, &.Mui-focused': {
          backgroundColor: palette.gray_700,
        },
      },
      '&.Mui-focused': {
        outline: `1px solid ${palette.gray_25}`,
      },
      transition: theme.transitions.create(['backgroundColor']),
    },
    input: {
      cursor: 'text',
      borderRadius: theme.spacing(0.5),
      '&:not(&.Mui-focused)': {
        textOverflow: 'ellipsis',
      },
      padding: theme.spacing(0.5),
      maxWidth: theme.spacing(25),
      width: inputWidth || theme.spacing(2),
      '&.Mui-focused': {
        overflowX: 'auto',
        textOverflow: 'clip',
      },
      '&.Mui-disabled': {
        cursor: 'default',
      },
    },
    focus: {
      outline: `2px solid ${palette.mint_200} !important`,
      borderRadius: theme.spacing(0.5),
      '& > .MuiInput-root': {
        outline: 'none !important',
      },
    },
  })
);

const EditableTextLabel = (props: EditableTextLabelProps) => {
  const {
    id,
    value,
    disabled,
    ariaLabel,
    tooltipTitle,
    onChange,
    size,
    maxLength,
  } = props;
  /**
   * Handle value while user is typing
   */
  const [editValue, setEditValue] = useState(value);

  const [isEditing, setEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value);
    }
  }, [value, isEditing]);
  const inputId = useMemo(() => `${id}-editable-text`, [id]);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Need to calculate width of input so that it grows up to a max width
   * when user types - due to input html element enforcing its own width ~131px
   *
   */
  const getTextWidth = useCallback(
    (text: string, element?: HTMLElement) =>
      getWidth(text, getCanvasFont(element)),
    []
  );

  const [inputWidth, setInputWidth] = useState<number | undefined>(
    getTextWidth(value)
  );

  const { classes } = useStyles({ inputWidth, size });
  useKeyboardFocus({ ref: inputContainerRef, className: classes.focus });

  const valueToDisplay = useMemo(() => {
    if (!isEditing) {
      return value;
    }
    return editValue;
  }, [isEditing, editValue, value]);

  useEffect(() => {
    if (!inputRef.current) {
      return;
    }

    setInputWidth(getTextWidth(valueToDisplay, inputRef.current) ?? 0);
  }, [getTextWidth, valueToDisplay]);

  const [isTruncated, setIsTruncated] = useState(false);

  const updateTruncation = useCallback(() => {
    const el = inputRef.current;
    if (!el) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useLayoutEffect(() => {
    updateTruncation();
  }, [updateTruncation, valueToDisplay, inputWidth]);

  const tooltipContent = useMemo(() => {
    if (isEditing) {
      return tooltipTitle ?? null;
    }
    if (isTruncated && value) {
      return (
        <Box
          sx={{
            maxWidth: 360,
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
          }}
        >
          <Typography variant="pSm">{value}</Typography>
        </Box>
      );
    }
    return tooltipTitle ?? null;
  }, [isEditing, isTruncated, value, tooltipTitle]);

  const handleChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const text = e.currentTarget.value;
    setEditValue(text);
  };
  const handleFocus = () => {
    setEditing(true);
    setTimeout(() => {
      if (inputRef && inputRef.current) {
        inputRef.current.select();
      }
    }, 0);
  };
  const handleBlur = useCallback(() => {
    if (isEditing) {
      setEditing(false);
      if (editValue && editValue.trim().length > 0) {
        onChange(editValue);
      }
    }
  }, [editValue, isEditing, onChange]);

  const handleKeydown: React.KeyboardEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        if (e.key === 'Enter' && isEditing) {
          e.currentTarget.blur();
        }
        if (e.key === 'Escape' && isEditing) {
          setEditing(false);
          setEditValue(value);
          // Setting on next loop, so that when blur event is captured,
          // isEditing state is actually updated
          setTimeout(
            (element: HTMLInputElement) => {
              element.blur();
            },
            1,
            e.currentTarget
          );
        }
      },
      [isEditing, value]
    );

  return (
    <Tooltip
      text={tooltipContent}
      placement="top-start"
      disabled={!tooltipContent}
    >
      <TextField
        variant="standard"
        id={inputId}
        ref={inputContainerRef}
        spellCheck={false}
        disabled={disabled}
        value={valueToDisplay}
        onChange={handleChange}
        onFocus={handleFocus}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: classes.root,
            input: classes.input,
          },
          onKeyDown: handleKeydown,
          onBlur: handleBlur,
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
          'aria-label': ariaLabel,
          maxLength,
          onMouseEnter: updateTruncation,
        }}
        inputRef={inputRef}
        fullWidth={false}
      />
    </Tooltip>
  );
};

export default EditableTextLabel;
